import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert, CircularProgress, Divider } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { getUserWatchedQuery, getUserWatchlistQuery } from '../../graphql/watchlist';
import { 
  getUserMovieInteraction,
  markAsWatched,
  unmarkAsWatched,
  addToWatchlist,
  removeFromWatchlist
} from '../../services/movieInteractionApi';

const LegacyTableTest = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [watchedItems, setWatchedItems] = useState([]);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [interaction, setInteraction] = useState(null);

  // Test movie data
  const testMovie = {
    id: '507f1f77bcf86cd799439013', // Another test movie ID
    name: 'Legacy Table Test Movie',
    title: 'Legacy Table Test Movie'
  };

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date() }]);
  };

  const loadAllData = async () => {
    if (!user?.id) return;
    
    try {
      const client = generateClient();
      
      // Load UserMovieInteraction
      const interactionResult = await getUserMovieInteraction(user.id, testMovie.id);
      setInteraction(interactionResult);
      
      // Load WatchedItem table entries
      const watchedResult = await client.graphql({
        query: getUserWatchedQuery,
        variables: { userID: user.id }
      });
      const userWatchedItems = watchedResult.data?.listWatchedItems?.items?.filter(
        item => item.movieID === testMovie.id
      ) || [];
      setWatchedItems(userWatchedItems);
      
      // Load WatchlistItem table entries
      const watchlistResult = await client.graphql({
        query: getUserWatchlistQuery,
        variables: { userID: user.id }
      });
      const userWatchlistItems = watchlistResult.data?.listWatchlistItems?.items?.filter(
        item => item.movieID === testMovie.id
      ) || [];
      setWatchlistItems(userWatchlistItems);
      
      addResult('Load Data', true, 
        `Interaction: ${interactionResult ? 'Found' : 'None'}, WatchedItems: ${userWatchedItems.length}, WatchlistItems: ${userWatchlistItems.length}`);
    } catch (error) {
      addResult('Load Data', false, error.message);
    }
  };

  const testWatchedSync = async () => {
    if (!user?.id) return;
    setLoading(true);
    
    try {
      // Step 1: Mark as watched
      addResult('Step 1', true, 'Marking as watched...');
      await markAsWatched(user.id, testMovie.id);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for sync
      await loadAllData();
      
      const watchedAfterMark = watchedItems.length;
      addResult('Mark as Watched Sync', watchedAfterMark > 0, 
        `WatchedItem entries after marking: ${watchedAfterMark} ${watchedAfterMark > 0 ? '✅' : '❌'}`);
      
      // Step 2: Unmark as watched
      addResult('Step 2', true, 'Unmarking as watched...');
      await unmarkAsWatched(user.id, testMovie.id);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for cleanup
      await loadAllData();
      
      const watchedAfterUnmark = watchedItems.length;
      addResult('Unmark as Watched Cleanup', watchedAfterUnmark === 0, 
        `WatchedItem entries after unmarking: ${watchedAfterUnmark} ${watchedAfterUnmark === 0 ? '✅ CLEANED' : '❌ NOT CLEANED'}`);
      
    } catch (error) {
      addResult('Watched Sync Test', false, error.message);
    } finally {
      setLoading(false);
    }
  };

  const testWatchlistSync = async () => {
    if (!user?.id) return;
    setLoading(true);
    
    try {
      // Step 1: Add to watchlist
      addResult('Step 1', true, 'Adding to watchlist...');
      await addToWatchlist(user.id, testMovie.id);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for sync
      await loadAllData();
      
      const watchlistAfterAdd = watchlistItems.length;
      addResult('Add to Watchlist Sync', watchlistAfterAdd > 0, 
        `WatchlistItem entries after adding: ${watchlistAfterAdd} ${watchlistAfterAdd > 0 ? '✅' : '❌'}`);
      
      // Step 2: Remove from watchlist
      addResult('Step 2', true, 'Removing from watchlist...');
      await removeFromWatchlist(user.id, testMovie.id);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for cleanup
      await loadAllData();
      
      const watchlistAfterRemove = watchlistItems.length;
      addResult('Remove from Watchlist Cleanup', watchlistAfterRemove === 0, 
        `WatchlistItem entries after removing: ${watchlistAfterRemove} ${watchlistAfterRemove === 0 ? '✅ CLEANED' : '❌ NOT CLEANED'}`);
      
    } catch (error) {
      addResult('Watchlist Sync Test', false, error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  useEffect(() => {
    if (user?.id) {
      loadAllData();
    }
  }, [user?.id]);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to test legacy table functionality</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Legacy Table Sync Test
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This test verifies that the modern UserMovieInteraction table properly syncs with legacy WatchedItem and WatchlistItem tables.
      </Alert>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current State
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Test Movie ID: {testMovie.id}
          </Typography>
          
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2">UserMovieInteraction (Modern):</Typography>
              {interaction ? (
                <Box>
                  <Typography variant="body2">
                    • Watched: {interaction.watched ? '✅' : '❌'}
                  </Typography>
                  <Typography variant="body2">
                    • In Watchlist: {interaction.inWatchlist ? '✅' : '❌'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No interaction found
                </Typography>
              )}
            </Box>
            
            <Box>
              <Typography variant="subtitle2">WatchedItem (Legacy):</Typography>
              <Typography variant="body2" color={watchedItems.length > 0 ? 'success.main' : 'text.secondary'}>
                • Count: {watchedItems.length} entries
              </Typography>
              {watchedItems.map((item, index) => (
                <Typography key={index} variant="caption" display="block" color="text.secondary">
                  ID: {item.id.slice(-8)}...
                </Typography>
              ))}
            </Box>
            
            <Box>
              <Typography variant="subtitle2">WatchlistItem (Legacy):</Typography>
              <Typography variant="body2" color={watchlistItems.length > 0 ? 'success.main' : 'text.secondary'}>
                • Count: {watchlistItems.length} entries
              </Typography>
              {watchlistItems.map((item, index) => (
                <Typography key={index} variant="caption" display="block" color="text.secondary">
                  ID: {item.id.slice(-8)}...
                </Typography>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="outlined" 
          onClick={loadAllData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={testWatchedSync}
          disabled={loading}
        >
          Test Watched Sync & Cleanup
        </Button>
        
        <Button 
          variant="contained" 
          color="secondary"
          onClick={testWatchlistSync}
          disabled={loading}
        >
          Test Watchlist Sync & Cleanup
        </Button>
        
        <Button 
          variant="outlined" 
          color="error"
          onClick={clearResults}
        >
          Clear Results
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
            Testing sync operations...
          </Typography>
        </Box>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          
          {testResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No tests run yet
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              {testResults.map((result, index) => (
                <Box key={index}>
                  <Alert 
                    severity={result.success ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">
                      {result.test} - {result.timestamp.toLocaleTimeString()}
                    </Typography>
                    <Typography variant="body2">
                      {result.message}
                    </Typography>
                  </Alert>
                  {(index + 1) % 3 === 0 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LegacyTableTest;